<script setup lang="ts">
import Quill from "quill";
import { useWordProcessorStore } from "../useWordProcessorStore";
import { useTextColorDelta } from "~/composables/useTextColorDelta";

const wordProcessorInstanceId = inject("instanceId") as string;
const wordProcessorStore = useWordProcessorStore(wordProcessorInstanceId);
const { selectionRange, quillDelta } = storeToRefs(wordProcessorStore);

const editorRef = inject("quillEditor") as any;

const activeColor = computed(() => {
  const quill = editorRef.value?.getQuill?.();
  if (!quill) return "";

  const selection = selectionRange.value;
  if (!selection) return "";

  const format = quill.getFormat(selection.index, selection.length);
  return format.color || "";
});

// Helper function to convert hex color to rgba with transparency
const hexToRgba = (hex: string, alpha: number) => {
  if (!hex) return "";
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const onColorSelected = (color: string) => {
  const quill = editorRef.value?.getQuill?.();
  if (!quill) return;

  const selection = quill.getSelection();

  if (!selection) return;


  if(selection.index == undefined || selection.length == undefined) return;

  const history = quill.getModule("history");
  history?.cutoff();
  quill.updateContents(useTextColorDelta(
    quill.getContents(selection.index, selection.length), selection.index, color
  ), Quill.sources.USER);
  history?.cutoff();
};
</script>

<template>
  <!-- Text Color Picker -->
  <BasePopover align="start" trigger-class="!w-[30px] !h-[30px] !p-0 rounded">
    <div
      v-tooltip="'Text Color'"
      class="w-[30px] h-[30px] rounded hover:bg-light flex items-center justify-center relative"
      :style="{
        backgroundColor: hexToRgba(activeColor, 0.1),
      }"
    >
      <icon name="lucide:palette" size="18" />
      <!-- <div
              v-if="activeColor"
              class="absolute bottom-[2px] left-1/2 transform -translate-x-1/2 w-4 h-1 rounded"
              :style="{ backgroundColor: activeColor }"
            /> -->
    </div>
    <template #content>
      <div class="p-2 w-[200px]">
        <div class="font-bold pb-1 border-b mb-2">Text Color</div>
        <div class="grid grid-cols-7 gap-2">
          <button
            v-for="color in [
              '#000000',
              '#e60000',
              '#ff9900',
              '#ffff00',
              '#008a00',
              '#0066cc',
              '#9933ff',
              '#ffffff',
              '#facccc',
              '#ffebcc',
              '#ffffcc',
              '#cce8cc',
              '#cce0f5',
              '#ebd6ff',
              '#bbbbbb',
              '#f06666',
              '#ffc266',
              '#ffff66',
              '#66b966',
              '#66a3e0',
              '#c285ff',
              '#888888',
              '#a10000',
              '#b26b00',
              '#b2b200',
              '#006100',
              '#0047b2',
              '#6b24b2',
            ]"
            :key="color"
            class="w-4 h-4 rounded-md border hover:scale-110 transition-all cursor-pointer flex flex-col items-center justify-center"
            :class="{
              ' ring-4 ring-primary/30 shadow-lg shadow-primary/50 ':
                activeColor === color,
              'border-gray-300': activeColor !== color,
            }"
            :style="{ backgroundColor: color }"
            @click="onColorSelected(color)"
          />
        </div>
      </div>
    </template>
  </BasePopover>
</template>

<style scoped></style>
